import { useEffect, useRef } from "react"
import { useTranslation } from "@/clientWeb/i18n/LanguageContext"

interface LoadMoreSentinelProps {
  hasMore: boolean
  loading: boolean
  onVisible: () => void
}

/**
 * Tiny marker row at the end of a scoped contacts list. An IntersectionObserver
 * watches it (root: null, so it fires against whatever element actually
 * scrolls, not a specific container we'd have to thread a ref down to) and
 * calls `onVisible` the moment it enters view, which is how the list loads its
 * next page without a "Load more" button.
 *
 * Renders nothing once there is no further page, so a short list never grows
 * an empty strip at the bottom.
 */
export function LoadMoreSentinel({ hasMore, loading, onVisible }: LoadMoreSentinelProps) {
  const t = useTranslation()
  const ref = useRef<HTMLDivElement | null>(null)
  // Mirrors `loading` into the observer callback without re-subscribing the
  // observer every time it flips, and is what keeps a still-visible sentinel
  // from firing `onVisible` again for every intersection tick while a page is
  // already in flight.
  const loadingRef = useRef(loading)
  loadingRef.current = loading

  useEffect(() => {
    if (!hasMore) return
    const node = ref.current
    if (!node) return
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting) && !loadingRef.current) onVisible()
      },
      { root: null, rootMargin: '200px' },
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [hasMore, onVisible])

  if (!hasMore) return null

  return (
    <div ref={ref} className="py-3 flex items-center justify-center">
      {loading && <p className="text-xs text-muted-foreground">{t('presence.loading')}</p>}
    </div>
  )
}
